/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import useIntersectionObserver from '../hooks/useIntersectionObserver';
import { ArrowRight, Tag, Clock, ChevronLeft } from 'lucide-react';
import { blogPostsData as allBlogPosts } from './BlogPageData'; // Poprawny import metadanych
import { blogContent } from './BlogContentData'; // Poprawiony import TREŚCI
import { BlogPostCard } from './BlogPostCard';
import './BlogPostPage.css';

function BlogPostPage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [post, setPost] = useState(null);
    const [relatedPosts, setRelatedPosts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const [headerRef, isHeaderVisible] = useIntersectionObserver();
    const [contentRef, isContentVisible] = useIntersectionObserver();
    const [relatedRef, isRelatedVisible] = useIntersectionObserver();

    useEffect(() => {
        setIsLoading(true);
        const currentPost = allBlogPosts.find(p => p.slug === slug);
        setPost(currentPost);

        if (currentPost) {
            const related = allBlogPosts
                .filter(p => p.category === currentPost.category && p.id !== currentPost.id)
                .slice(0, 3);
            if (related.length < 3) {
                related.push(...allBlogPosts.filter(p => p.id !== currentPost.id && !related.some(rp => rp.id === p.id)).slice(0, 3 - related.length));
            }
            setRelatedPosts(related);
        } else {
             setRelatedPosts([]);
        }
        setIsLoading(false);
        window.scrollTo(0, 0);
    }, [slug]);

    const calculateReadingTime = (contentJSX) => {
        if (!React.isValidElement(contentJSX) || !contentJSX.props.children || typeof contentJSX.props.children === 'string') {
             if (post?.excerpt) {
                 const wordsPerMinute = 200;
                 const words = post.excerpt.split(/\s+/).filter(Boolean).length;
                 return Math.max(1, Math.ceil(words / wordsPerMinute));
             }
             return 1;
        }
        try {
             const getTextFromJsx = (element) => {
                 if (!element) return '';
                 if (typeof element === 'string') return element;
                 if (Array.isArray(element)) return element.map(getTextFromJsx).join(' ');
                 if (React.isValidElement(element) && element.props.children) {
                     return getTextFromJsx(element.props.children);
                 }
                 return '';
             };
             const textContent = getTextFromJsx(contentJSX);
             const wordsPerMinute = 200;
             const words = textContent.trim().split(/\s+/).filter(Boolean).length;
             const minutes = Math.ceil(words / wordsPerMinute);
             return Math.max(1, minutes);
         } catch (error) {
              console.error("Błąd podczas obliczania czasu czytania:", error);
              return 3;
         }
     };

    if (isLoading) {
        return <div className="container page-loading"><p>Ładowanie artykułu...</p></div>;
    }

    if (!post) {
        return (
             <div className="container post-not-found">
                <h2>404 - Artykuł nie znaleziony</h2>
                <p>Przepraszamy, artykuł o ścieżce "/blog/{slug}" nie został znaleziony.</p>
                <Link to="/blog" className="button-secondary">Wróć do Bloga</Link>
             </div>
        );
    }

    const articleContent = blogContent[post.slug] || <p>Treść w przygotowaniu.</p>;
    const readingTime = calculateReadingTime(articleContent);

    return (
        <>
            <article className="blog-post-page">
                <header ref={headerRef} className={`blog-post-header section-bg-light-gray animate-on-scroll ${isHeaderVisible ? 'is-visible' : ''}`}>
                    <div className="container">
                        <div className="post-meta-header">
                            <button onClick={() => navigate(-1)} className="back-to-blog-link">
                                <ChevronLeft size={16}/> Wróć do Bloga
                            </button>
                            {post.category && typeof post.category === 'string' && (
                                <span className="post-category-header">
                                    <Tag size={14}/> {post.category}
                                </span>
                            )}
                        </div>
                        <h1>{post.title}</h1>
                        <div className="post-meta-subheader">
                            {post.date && <span className="post-date"><Clock size={16}/> Opublikowano: {post.date}</span>}
                            {articleContent && typeof articleContent !== 'string' && <span className="reading-time"><Clock size={16}/> ok. {readingTime} min czytania</span>}
                        </div>
                    </div>
                </header>

                 <section ref={contentRef} className={`post-content-section section-bg-white animate-on-scroll ${isContentVisible ? 'is-visible' : ''}`}>
                    <div className="container content-narrow">
                        <div className="featured-image-container-inner">
                            {post.image && <img src={post.image} alt={post.title || 'Obrazek artykułu'} className="featured-image-inner"/>}
                        </div>
                        <div className="post-content">
                           {articleContent}
                        </div>
                    </div>
                </section>

                {relatedPosts.length > 0 && (
                    <section ref={relatedRef} className={`related-posts-section section-bg-light-gray animate-on-scroll ${isRelatedVisible ? 'is-visible' : ''}`}>
                        <div className="container">
                            <h2 className="text-center">Przeczytaj również</h2>
                            <div className="blog-grid related-posts-grid">
                                {relatedPosts.map((relatedPost) => (
                                    <BlogPostCard key={relatedPost.id} post={relatedPost} />
                                ))}
                            </div>
                        </div>
                    </section>
                )}
            </article>
        </>
    );
}

export default BlogPostPage;